import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Download, ExternalLink, Eye, Calendar, FileText, Share2, Check, BookMarked } from 'lucide-react';
import SEO from '@/components/common/SEO';
import { books } from '@/data/books';

const BookDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const book = books.find((b) => b.id === parseInt(id, 10));
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: book?.title, text: book?.description, url });
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!book) {
    return (
      <div className="min-h-screen bg-[#080d14] text-white pt-20 flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto">
            <BookOpen size={36} className="text-gray-600" />
          </div>
          <h1 className="text-4xl font-bold">Book Not Found</h1>
          <button
            onClick={() => navigate('/#books')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-semibold transition-colors"
          >
            <ArrowLeft size={18} /> Back to Books
          </button>
        </div>
      </div>
    );
  }

  const coverUrl = book.cover
    ? (book.cover.startsWith('http') ? book.cover : book.cover)
    : null;

  return (
    <div className="min-h-screen bg-[#080d14] text-white pt-20">
      <SEO
        title={book.title}
        description={book.description || `${book.title}${book.subtitle ? ' — ' + book.subtitle : ''}`}
        image={book.cover || '/images/image1.jpg'}
        url={`/books/${book.id}`}
        type="book"
      />

      <div className="max-w-4xl mx-auto px-4">
        <div className="py-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-400 text-sm font-medium transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            Back
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-[280px_1fr] gap-10 pb-24"
        >
          {/* Cover */}
          <div className="flex-shrink-0">
            <div className="rounded-2xl overflow-hidden border border-white/8 shadow-2xl shadow-black/40 aspect-[3/4]">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={book.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-stretch">
                  <div className="w-7 bg-emerald-700 flex-shrink-0" />
                  <div className="flex-1 bg-gray-900 flex flex-col justify-between p-6 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 left-0 right-0 h-px bg-emerald-400" />
                      <div className="absolute bottom-16 left-0 right-0 h-px bg-emerald-400" />
                    </div>
                    <span className="text-emerald-400 text-xs font-semibold uppercase tracking-widest">
                      {book.genre || "Book"}
                    </span>
                    <div>
                      <h4 className="text-white text-2xl font-bold leading-snug mb-3">
                        {book.title}
                      </h4>
                      {book.subtitle && (
                        <p className="text-gray-400 text-sm italic leading-relaxed">{book.subtitle}</p>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs tracking-wide">Boye Emmanuel Tete</p>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-5 space-y-3">
              {book.pdfUrl && (
                <a
                  href={book.pdfUrl}
                  download
                  className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg shadow-emerald-500/20"
                >
                  <Download size={18} /> Download PDF
                </a>
              )}
              {book.readUrl && (
                <a
                  href={book.readUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-white/8 border border-emerald-500/30 hover:bg-emerald-500/10 hover:border-emerald-400 text-emerald-400 font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  <BookMarked size={18} /> Read Book
                </a>
              )}
              {book.purchaseUrl && (
                <a
                  href={book.purchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-white/8 border border-white/12 hover:bg-white/12 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  <ExternalLink size={18} /> Buy Now
                </a>
              )}
              {book.previewUrl && (
                <a
                  href={book.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/8 hover:bg-white/10 text-gray-300 font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  <Eye size={18} /> Preview
                </a>
              )}
              <button
                onClick={handleShare}
                className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-emerald-500/40 text-gray-300 hover:text-emerald-400 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
              >
                {copied ? <><Check size={18} className="text-emerald-400" /> Link Copied!</> : <><Share2 size={18} /> Share Book</>}
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="space-y-6">
            {book.genre && (
              <span className="inline-block px-3 py-1 bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full tracking-wide">
                {book.genre}
              </span>
            )}

            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight tracking-tight">
                {book.title}
              </h1>
              {book.subtitle && (
                <p className="text-lg text-gray-400 italic mt-2">{book.subtitle}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500 border-t border-white/6 pt-5">
              {book.publishedDate && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-emerald-500" />
                  {new Date(book.publishedDate).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long',
                  })}
                </span>
              )}
              {book.pages && (
                <span className="flex items-center gap-1.5">
                  <FileText size={14} className="text-emerald-500" />
                  {book.pages} pages
                </span>
              )}
            </div>

            {book.description && (
              <p className="text-gray-300 leading-relaxed text-base">{book.description}</p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookDetail;
